import { Box, Button, CircularProgress, MenuItem, TextField, Typography, useTheme } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { useUpdateProfile } from '../hooks/UseUpdateProfile';
import UploadImage from '../images/icons/upload-image.png';
import { useCallback, useContext, useEffect, useState } from 'react';
import AuthContext from '../context/AuthContext';
import { useWaits } from '../hooks/UseWait';
import { useConstants } from '../hooks/UseConstants';
import Fetch from '../services/Fetch';
import { buildProfileFormData } from '../helper/ProfileFormData';

function UpdateProfile({ onClickCancel, setSnackBar }) {
    const { host, language } = useConstants();
    const { profile, setProfile } = useContext(AuthContext);
    const {
        firstName, setFirstName, lastName, setLastName, code, setCode,
        phoneNumber, setPhoneNumber, image, setImage, birthDate, setBirthDate, bio, setBio,
        majorId, setMajorId, academicDegreeId, setAcademicDegreeId,
        specializationId, setSpecializationId, experienceYears, setExperienceYears,
    } = useUpdateProfile();
    const { sendWait, setSendWait } = useWaits();
    const theme = useTheme();
    const isAr = language === 'ar';
    const [majors, setMajors] = useState([]);
    const [academicDegrees, setAcademicDegrees] = useState([]);
    const [specializations, setSpecializations] = useState([]);

    const handleChange = (value, country) => {
        const numberWithoutCode = value.replace(country.dialCode, '');
        setCode(country.dialCode);
        setPhoneNumber(numberWithoutCode);
    };

    const setInputs = useCallback(() => {
        setFirstName(profile.first_name || '');
        setLastName(profile.last_name || '');
        setCode(profile.phone_code || profile.code || '');
        setPhoneNumber(profile.phone || '');
        setBirthDate(profile.birth_date || '');
        setBio(profile.bio || '');
        setMajorId(profile.major?.id ? String(profile.major.id) : '');
        setAcademicDegreeId(profile.academic_degree?.id ? String(profile.academic_degree.id) : '');
        setSpecializationId(profile.specialization?.id ? String(profile.specialization.id) : '');
        setExperienceYears(profile.experience_years != null ? String(profile.experience_years) : '');
    }, [
        profile,
        setAcademicDegreeId,
        setBio,
        setBirthDate,
        setCode,
        setExperienceYears,
        setFirstName,
        setLastName,
        setMajorId,
        setPhoneNumber,
        setSpecializationId,
    ]);

    const updateProfile = async () => {
        setSendWait(true);
        const formData = buildProfileFormData({
            firstName,
            lastName,
            image,
            phoneCode: code,
            phoneNumber,
            email: profile.email,
            birthDate,
            language: profile.language,
            bio,
            majorId,
            academicDegreeId,
            specializationId,
            experienceYears,
        });

        const result = await Fetch(host + '/account/update-profile', 'POST', formData);

        if (result.status === 200) {
            setProfile(result.data.data);
            setSnackBar('success', isAr ? 'تم التحديث بنجاح' : 'Updated successfully');
            onClickCancel();
        } else if (result.status === 422) {
            setSnackBar('error', result.data.errors[0]);
        }

        setSendWait(false);
    };

    useEffect(() => {
        setInputs();
    }, [setInputs]);

    useEffect(() => {
        const loadAcademicOptions = async () => {
            const [majorsResult, degreesResult, specializationsResult] = await Promise.all([
                Fetch(`${host}/majors?per_page=200`, 'GET', null),
                Fetch(`${host}/academic-degrees?per_page=100`, 'GET', null),
                Fetch(`${host}/teacher-specializations?per_page=100`, 'GET', null),
            ]);

            if (majorsResult.status === 200) {
                setMajors(majorsResult.data?.data || []);
            }

            if (degreesResult.status === 200) {
                setAcademicDegrees(degreesResult.data?.data || []);
            }

            if (specializationsResult.status === 200) {
                setSpecializations(specializationsResult.data?.data || []);
            }
        };

        loadAcademicOptions();
    }, [host]);

    return (
        <Box sx={{ background: theme.palette.background.paper }} className="w-1/2 h-3/4 rounded-xl py-3 px-5 overflow-y-scroll none-view-scroll max-sm:w-5/6">
            <Box className="w-20 h-20 rounded-full mx-auto my-5 flex justify-center items-center" sx={{ background: '#E8EEFD' }}>
                <EditOutlinedIcon className="text-blue-500 !text-5xl" />
            </Box>
            <Typography variant="h5" fontWeight={800} className="text-center">
                {isAr ? 'تحديث الملف الشخصي' : 'Update profile'}
            </Typography>
            <Typography variant="body1" className="text-center !mt-5 text-gray-700">
                {isAr ? 'حدّث معلوماتك الأساسية' : 'Update your basic account information'}
            </Typography>
            <TextField className="w-full !mt-5" label={isAr ? 'الاسم الأول' : 'First name'} onChange={(e) => setFirstName(e.target.value)} value={firstName} />
            <TextField className="w-full !mt-5" label={isAr ? 'اسم العائلة' : 'Last name'} onChange={(e) => setLastName(e.target.value)} value={lastName} />
            <TextField type="date" className="w-full !mt-5" label={isAr ? 'تاريخ الميلاد' : 'Birth date'} onChange={(e) => setBirthDate(e.target.value)} value={birthDate} InputLabelProps={{ shrink: true }} />
            <TextField
                select
                className="w-full !mt-5"
                label={isAr ? 'الدرجة الأكاديمية' : 'Academic degree'}
                value={academicDegreeId}
                onChange={(e) => setAcademicDegreeId(e.target.value)}
            >
                {academicDegrees.map((degree) => (
                    <MenuItem key={degree.id} value={degree.id}>
                        {degree.name_ar || degree.name_en}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                select
                className="w-full !mt-5"
                label={isAr ? 'التخصص الأكاديمي' : 'Academic major'}
                value={majorId}
                onChange={(e) => setMajorId(e.target.value)}
            >
                {majors.map((major) => (
                    <MenuItem key={major.id} value={major.id}>
                        {major.name_ar || major.name_en}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                select
                className="w-full !mt-5"
                label={isAr ? 'التخصص التدريسي' : 'Teaching specialization'}
                value={specializationId}
                onChange={(e) => setSpecializationId(e.target.value)}
            >
                {specializations.map((specialization) => (
                    <MenuItem key={specialization.id} value={specialization.id}>
                        {specialization.name_ar || specialization.name_en}
                    </MenuItem>
                ))}
            </TextField>
            <TextField
                type="number"
                className="w-full !mt-5"
                label={isAr ? 'سنوات الخبرة' : 'Years of experience'}
                value={experienceYears}
                onChange={(e) => setExperienceYears(e.target.value)}
                inputProps={{ min: 0 }}
            />
            <Box dir="ltr" className="w-full h-14 mt-5 max-sm:h-12">
                <PhoneInput
                    country="sy"
                    value={code + phoneNumber}
                    containerStyle={{ width: '100%' }}
                    buttonStyle={{ background: theme.palette.mode === 'dark' ? 'none' : '' }}
                    inputStyle={{ width: '100%', height: '100%', color: theme.palette.mode === 'dark' ? 'white' : 'black', background: 'none' }}
                    onChange={handleChange}
                />
            </Box>
            <TextField className="w-full !mt-5" label={isAr ? 'السيرة الذاتية' : 'Bio'} multiline minRows={2} value={bio} onChange={(e) => setBio(e.target.value)} />
            <Box className="relative w-full h-32 bg-gray-200 rounded-xl mt-5 flex flex-col items-center justify-center cursor-pointer">
                <img src={UploadImage} alt="" />
                <Typography variant="body1" className="text-gray-700">{isAr ? 'إضافة صورة' : 'Add image'}</Typography>
                <input type="file" accept="image/*" className="w-full h-full opacity-0 absolute cursor-pointer" onChange={(e) => setImage(e.target.files[0])} />
            </Box>
            <Box className="py-3 flex justify-between max-sm:flex-col" sx={{ flexDirection: language === 'en' ? 'row-reverse' : 'row' }}>
                <Button onClick={updateProfile} variant="contained" className="w-2/5 !py-3 flex max-sm:w-full !text-white">
                    {sendWait ? (
                        <CircularProgress size={20} color="inherit" />
                    ) : (
                        <>
                            {isAr ? 'حفظ' : 'Save'}
                            <SaveOutlinedIcon className="ml-2" />
                        </>
                    )}
                </Button>
                <Button onClick={() => { setInputs(); onClickCancel(); }} variant="contained" className="w-2/5 !py-3 flex !text-gray-500 !font-bold max-sm:w-full max-sm:!mt-3" color="inherit">
                    {isAr ? 'إلغاء' : 'Cancel'}
                </Button>
            </Box>
        </Box>
    );
}

export default UpdateProfile;
